import {
  commandeStatusRemovesLinkedDelivery,
  formatProDocCode,
  PRO_COMMANDE_SEQ,
  type CommandeStatus,
} from '@erp/domain/pro';
import {
  netUnitCostHt,
  resolveSalesDeliveryWarehouseId,
} from '@erp/domain/sales';
import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import { fetchAllRows } from './fetch-all-rows';
import type {
  CreateProCommandeInput,
  IProCommandeRepository,
  ProCommande,
  ProCommandeDetailProjection,
  ProCommandeFilters,
  ProCommandeLine,
  ProCommandeSummary,
  ProStatistics,
  SalesDocument,
  UpdateProCommandeInput,
} from '../../../interfaces/src';
import { SupabaseSalesRepository } from './sales-repository';
import { SupabaseStockRepository } from './stock-repository';
import { fetchProArticleSnapshots, type ProArticleSnapshot } from './pro-article-snapshots';

/**
 * Jour calendaire LOCAL en `YYYY-MM-DD`.
 *
 * Pas `toISOString().slice(0, 10)` : ça renvoie le jour UTC, donc la veille
 * entre minuit et 01h00 en Tunisie (UTC+1) — un document daté et numéroté
 * séquentiellement se retrouverait antidaté d'un jour.
 */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export class SupabaseProCommandeRepository implements IProCommandeRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: ProCommandeFilters): Promise<ProCommande[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = (this.supabase as any)
        .from('pro_commandes')
        .select('*, lines:pro_commande_lines(*), client:pro_clients(*)');
      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.from) query = query.gte('date', filters.from);
      if (filters?.to) query = query.lte('date', filters.to);
      if (filters?.search)
        query = query.or(orContainsAnyColumn(['code'], filters.search));
      return query
        .order('date', { ascending: false })
        .order('code', { ascending: false })
        .range(from, to);
    });
    const snapshots = await fetchProArticleSnapshots(this.supabase, collectArticleIds(rows));
    return rows.map((row) => mapCommande(row, snapshots));
  }

  async listSummaries(filters?: ProCommandeFilters): Promise<ProCommandeSummary[]> {
    // `code` est la clé primaire : l'ajouter en DERNIER critère de tri rend
    // l'ordre TOTAL, sans quoi `.range()` sert deux fois les lignes de même
    // date et en saute d'autres (cf. withStableOrder / fetch-all-rows).
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = (this.supabase as any)
        .from('pro_commandes')
        .select(
          'code, date, status, pro_customer_id, pro_client_id, parent_devis_code, delivery_city, pht, pttc, total_commission, delivery_fee, total_used, total_a_payer',
        );
      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.from) query = query.gte('date', filters.from);
      if (filters?.to) query = query.lte('date', filters.to);
      if (filters?.search) query = query.or(orContainsAnyColumn(['code'], filters.search));
      return query
        .order('date', { ascending: false })
        .order('code', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapCommandeSummary);
  }

  async getByCode(
    code: string,
    projection?: ProCommandeDetailProjection,
  ): Promise<ProCommande | null> {
    const sources = projection ?? {
      includeCustomer: true,
      includeProducts: true,
      includeArticleSnapshots: true,
      includeLinkedDelivery: true,
    };
    const select = [
      '*',
      sources.includeProducts ? 'lines:pro_commande_lines(*)' : null,
      sources.includeCustomer ? 'client:pro_clients(*)' : null,
    ].filter((column): column is string => column !== null).join(', ');
    const { data, error } = await (this.supabase as any)
      .from('pro_commandes')
      .select(select)
      .eq('code', code)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    if (!data) return null;
    const snapshots = sources.includeProducts && sources.includeArticleSnapshots
      ? await fetchProArticleSnapshots(this.supabase, collectArticleIds([data]))
      : undefined;
    const order = mapCommande(data, snapshots);
    if (!sources.includeLinkedDelivery) {
      return { ...order, deliverySalesDocumentId: null };
    }
    if (order.deliverySalesDocumentId) return order;

    const linkedDelivery = await this.getLinkedDeliveryByCommandeCode(code);
    if (!linkedDelivery) return order;
    await this.linkDeliveryToCommande(code, linkedDelivery.id);
    return { ...order, deliverySalesDocumentId: linkedDelivery.id };
  }

  async createDeliveryFromCommande(code: string): Promise<SalesDocument> {
    const order = await this.getByCode(code);
    if (!order) throw new Error('Commande introuvable');
    const salesRepository = new SupabaseSalesRepository(this.supabase);

    if (order.deliverySalesDocumentId) {
      const existing = await salesRepository.getDocumentById(order.deliverySalesDocumentId);
      if (existing) return existing;
    }

    const linkedByCode = await this.getLinkedDeliveryByCommandeCode(code);
    if (linkedByCode) {
      await this.linkDeliveryToCommande(code, linkedByCode.id);
      return linkedByCode;
    }

    const articleMap = await this.getArticleMap(order.lines?.map((l) => l.articleId) ?? []);
    const warehouses = await new SupabaseStockRepository(this.supabase)
      .listWarehouses();
    const stockWarehouseId = resolveSalesDeliveryWarehouseId(
      order.lines?.map((line) => ({ articleId: line.articleId })) ?? [],
      warehouses,
    );
    if ((order.lines?.length ?? 0) > 0 && !stockWarehouseId) {
      throw new Error('sales_delivery_warehouse_required');
    }
    const totalDiscount = (order.lines ?? []).reduce((sum, line) => sum + line.montantRemise, 0);
    let delivery: SalesDocument;
    try {
      delivery = await salesRepository.createDocument({
        docType: 'delivery',
        customerSource: 'pro',
        clientId: null,
        proCustomerId: order.proCustomerId,
        proClientId: order.proClientId,
        stockWarehouseId,
        // Le BL est daté du jour où la commande Pro est transformée, pas du jour
        // de la commande — même règle que les transformations côté ERP.
        docDate: todayISO(),
        notes: order.notes ?? undefined,
        totalHt: order.pht,
        totalDiscount,
        totalVat: order.tva,
        totalTtc: order.pttc,
        deliveryFee: order.deliveryFee,
        netToPay: order.totalAPayer,
        proCommandeCode: order.code,
        lines: (order.lines ?? []).map((line) => {
          const article = articleMap.get(line.articleId);
          const quantity = Math.max(line.quantite, 0);
          return {
            articleId: line.articleId,
            description: article?.name ?? line.articleId,
            quantity: line.quantite,
            unitPriceHt: grossUnitPriceHt(line.pht, quantity, line.remise),
            // Coût FIGÉ à la commande (jamais le catalogue vivant, jamais le
            // brut) : le BL hérite du snapshot pour que la commission promise
            // au checkout reste la commission recalculée à la livraison.
            purchasePrice: line.unitCostHt ?? article?.netCostHt ?? 0,
            discountPct: line.remise,
            vatRate: line.tva,
            totalHt: line.pht,
            totalTtc: line.pttc,
          };
        }),
      });
    } catch (error) {
      const recovered = await this.getLinkedDeliveryByCommandeCode(code);
      if (!recovered) throw error;
      await this.linkDeliveryToCommande(code, recovered.id);
      return recovered;
    }

    await this.linkDeliveryToCommande(code, delivery.id);
    return delivery;
  }

  private async linkDeliveryToCommande(code: string, deliveryId: string): Promise<void> {
    const { error } = await (this.supabase as any)
      .from('pro_commandes')
      .update({ delivery_sales_document_id: deliveryId })
      .eq('code', code);
    if (error) throw error;
  }

  private async getLinkedDeliveryByCommandeCode(code: string): Promise<SalesDocument | null> {
    const { data, error } = await (this.supabase as any)
      .from('authorized_sales_documents')
      .select('id')
      .eq('doc_type', 'delivery')
      .eq('pro_commande_code', code)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const id = (data?.id as string | undefined) ?? null;
    if (!id) return null;
    return new SupabaseSalesRepository(this.supabase).getDocumentById(id);
  }

  async create(input: CreateProCommandeInput): Promise<ProCommande> {
    const code = await this.getNextCode(new Date().getFullYear());
    const { error } = await (this.supabase as any).rpc('pro_create_commande_atomic', {
      p_code: code,
      p_pro_client_id: input.proClientId ?? null,
      p_parent_devis_code: input.parentDevisCode ?? null,
      p_status: 'en_attente',
      p_pht: input.pht,
      p_pttc: input.pttc,
      p_tva: input.tva,
      p_taux_remise: input.tauxRemise,
      p_type_remise: input.typeRemise,
      p_total_commission: input.totalCommission,
      p_delivery_fee: input.deliveryFee,
      p_total_used: input.totalUsed,
      p_total_a_payer: input.totalAPayer,
      p_used_solde: input.usedSolde,
      p_delivery_address: input.deliveryAddress ?? null,
      p_delivery_city: input.deliveryCity ?? null,
      p_delivery_postal_code: input.deliveryPostalCode ?? null,
      p_delivery_complement: input.deliveryComplement ?? null,
      // null depuis l'Espace Pro (le serveur déduit l'électricien de la
      // session) ; renseigné par le back-office qui saisit pour son compte.
      p_pro_customer_id: input.proCustomerId ?? null,
      p_lines: input.lines.map((l) => ({
        article_id: l.articleId,
        quantite: l.quantite,
        pht: l.pht,
        pttc: l.pttc,
        tva: l.tva,
        remise: l.remise,
        montant_remise: l.montantRemise,
      })),
    });
    if (error) throw error;
    const created = await this.getByCode(code);
    if (!created) throw new Error('Commande créée mais introuvable');
    return created;
  }

  async updateStatus(code: string, status: CommandeStatus): Promise<ProCommande> {
    const { error } = await (this.supabase as any)
      .from('pro_commandes')
      .update({ status })
      .eq('code', code);
    if (error) throw error;

    // Commande rejetée ou retournée ⇒ son BL est supprimé (stock restauré,
    // imputations libérées — règle « commande annulée ⇒ BL retiré »). La FK
    // delivery_sales_document_id est ON DELETE SET NULL : la commande se délie
    // d'elle-même. Le garde-fou côté ventes laisse le BL en place s'il est
    // déjà facturé ou porte un avoir. En cas d'échec ici, le statut est déjà
    // posé et l'erreur remonte ; un BL restant demeure visible (« Ouvrir le
    // BL ») et supprimable à la main.
    if (commandeStatusRemovesLinkedDelivery(status)) {
      const deliveryId = await this.getLinkedDeliveryId(code);
      if (deliveryId) {
        await new SupabaseSalesRepository(this.supabase).deleteDeliveryForCancelledOrder(deliveryId);
      }
    }

    const fresh = await this.getByCode(code);
    if (!fresh) throw new Error('Commande introuvable');
    return fresh;
  }

  /**
   * Suppression définitive d'une commande Espace Pro.
   *
   * L'ordre compte, et il est dicté par ce qu'un échec à mi-chemin laisserait
   * derrière lui :
   *  1. le BL d'abord — c'est le seul refus possible, et il doit intervenir
   *     avant qu'on ait touché à quoi que ce soit ;
   *  2. les mouvements de portefeuille — la commission créditée et le solde
   *     consommé sont rendus. `reference_code` n'a PAS de clé étrangère : sans
   *     ce nettoyage, l'électricien garderait le crédit d'une commande
   *     disparue, et le solde de la vue `pro_customer_wallet_view` mentirait ;
   *  3. les notifications qui pointent vers `/ordres/<code>`, dont le lien
   *     tomberait dans le vide ;
   *  4. la commande elle-même — ses lignes suivent par cascade.
   *
   * Un échec après l'étape 1 laisse une commande sans BL : incohérent, mais
   * réparable en relançant la suppression. L'ordre inverse laisserait un BL
   * orphelin, lui irrattrapable une fois la commande partie.
   */
  async delete(code: string): Promise<void> {
    const deliveryId = await this.getLinkedDeliveryId(code);
    if (deliveryId) {
      await new SupabaseSalesRepository(this.supabase).removeDeliveriesForDeletedOrder({
        scope: 'pro',
        orderRef: code,
        deliveryIds: [deliveryId],
      });
    }

    const { error: walletError } = await (this.supabase as any)
      .from('pro_wallet_movements')
      .delete()
      .eq('reference_code', code);
    if (walletError) throw walletError;

    const { error: notifError } = await (this.supabase as any)
      .from('pro_notifications')
      .delete()
      .eq('link_url', `/ordres/${code}`);
    if (notifError) throw notifError;

    // `.select()` sur le DELETE : sans policy DELETE applicable, PostgREST
    // renvoie 0 ligne SANS erreur — la commande resterait à l'écran et
    // l'utilisateur croirait à un bug d'affichage. Même piège que
    // pro_customers.
    const { data: deleted, error } = await (this.supabase as any)
      .from('pro_commandes')
      .delete()
      .eq('code', code)
      .select('code');
    if (error) throw error;
    if (!deleted || (deleted as unknown[]).length === 0) {
      throw new Error(
        `Commande ${code} non supprimée : introuvable, ou droits insuffisants sur cet espace.`,
      );
    }
  }

  /** Id du BL lié : la colonne de liaison d'abord, sinon la recherche par
   *  pro_commande_code (BL créé avant que la liaison ne soit posée). */
  private async getLinkedDeliveryId(code: string): Promise<string | null> {
    const { data, error } = await (this.supabase as any)
      .from('pro_commandes')
      .select('delivery_sales_document_id')
      .eq('code', code)
      .maybeSingle();
    if (error) throw error;
    const linked = (data?.['delivery_sales_document_id'] as string | undefined) ?? null;
    if (linked) return linked;
    return (await this.getLinkedDeliveryByCommandeCode(code))?.id ?? null;
  }

  async update(code: string, input: UpdateProCommandeInput): Promise<ProCommande> {
    const existing = await this.getByCode(code);
    if (!existing) throw new Error('Commande introuvable');

    // Coût net FIGÉ par ligne : la valeur transmise par l'appelant, sinon le
    // snapshot déjà porté par la ligne existante (même article), sinon —
    // dernier recours, ligne vraiment nouvelle — le coût net catalogue du
    // moment. Un edit ne re-dérive JAMAIS le coût d'une ligne existante.
    const articleMap = await this.getArticleMap(input.lines.map((l) => l.articleId));
    const existingCostByArticle = new Map<string, number>();
    for (const line of existing.lines ?? []) {
      if (line.unitCostHt != null && line.unitCostHt > 0 && !existingCostByArticle.has(line.articleId)) {
        existingCostByArticle.set(line.articleId, line.unitCostHt);
      }
    }
    const frozenCosts = input.lines.map((line) => {
      const provided = Number(line.unitCostHt);
      if (Number.isFinite(provided) && provided > 0) return round3(provided);
      return existingCostByArticle.get(line.articleId)
        ?? articleMap.get(line.articleId)?.netCostHt
        ?? null;
    });

    const { error: updateError } = await (this.supabase as any)
      .from('pro_commandes')
      .update({
        pro_client_id: input.proClientId ?? null,
        pht: input.pht,
        pttc: input.pttc,
        tva: input.tva,
        taux_remise: input.tauxRemise,
        type_remise: input.typeRemise,
        total_commission: input.totalCommission,
        delivery_fee: input.deliveryFee,
        total_used: input.totalUsed,
        total_a_payer: input.totalAPayer,
        used_solde: input.usedSolde,
        delivery_address: input.deliveryAddress ?? null,
        delivery_city: input.deliveryCity ?? null,
        delivery_postal_code: input.deliveryPostalCode ?? null,
        delivery_complement: input.deliveryComplement ?? null,
        notes: input.notes ?? null,
      })
      .eq('code', code);
    if (updateError) throw updateError;

    const { error: deleteError } = await (this.supabase as any)
      .from('pro_commande_lines')
      .delete()
      .eq('commande_code', code);
    if (deleteError) throw deleteError;

    if (input.lines.length > 0) {
      const { error: insertError } = await (this.supabase as any)
        .from('pro_commande_lines')
        .insert(input.lines.map((line, idx) => ({
          tenant_id: existing.tenantId,
          commande_code: code,
          article_id: line.articleId,
          quantite: line.quantite,
          pht: line.pht,
          pttc: line.pttc,
          tva: line.tva,
          remise: line.remise,
          montant_remise: line.montantRemise,
          line_number: idx + 1,
          unit_cost_ht: frozenCosts[idx],
        })));
      if (insertError) throw insertError;
    }

    // La commission PENDING du portefeuille suit le montant recalculé — c'est
    // elle que l'admin approuve (le montant réellement crédité). Une commission
    // déjà approuvée (argent crédité) n'est jamais retouchée.
    const { error: movementError } = await (this.supabase as any)
      .from('pro_wallet_movements')
      .update({ amount: input.totalCommission })
      .eq('reference_code', code)
      .eq('type', 'commission')
      .eq('status', 'pending');
    if (movementError) throw movementError;

    const linkedDeliveryId = existing.deliverySalesDocumentId
      ?? (await this.getLinkedDeliveryByCommandeCode(code))?.id
      ?? null;
    if (linkedDeliveryId) {
      if (!existing.deliverySalesDocumentId) {
        await this.linkDeliveryToCommande(code, linkedDeliveryId);
      }
      await this.updateLinkedDelivery(linkedDeliveryId, code, existing.proCustomerId, input, frozenCosts, articleMap);
    }

    const fresh = await this.getByCode(code);
    if (!fresh) throw new Error('Commande introuvable');
    return fresh;
  }

  async getNextCode(year: number): Promise<string> {
    const { data, error } = await (this.supabase as any).rpc('get_next_pro_sequence_value', {
      p_seq_name: PRO_COMMANDE_SEQ,
    });
    if (error) throw error;
    return formatProDocCode('CMD', year, Number(data));
  }

  async getStatistics(): Promise<ProStatistics> {
    const { data: cmds, error } = await (this.supabase as any)
      .from('pro_commandes')
      .select('status, pttc, total_commission, date');
    if (error) throw error;
    const { count: devisCount } = await (this.supabase as any)
      .from('pro_devis')
      .select('*', { count: 'exact', head: true });

    const rows = (cmds ?? []) as Array<Record<string, unknown>>;
    const totals: { totalCommandes: number; totalRevenue: number; totalCommission: number; pendingCommandes: number } = {
      totalCommandes: 0,
      totalRevenue: 0,
      totalCommission: 0,
      pendingCommandes: 0,
    };
    for (const r of rows) {
      totals.totalCommandes += 1;
      totals.totalRevenue += Number(r['pttc'] ?? 0);
      totals.totalCommission += Number(r['total_commission'] ?? 0);
      if (r['status'] === 'en_attente') totals.pendingCommandes += 1;
    }

    // Group by month for the chart (last 6 months)
    const buckets = new Map<string, { count: number; revenue: number }>();
    for (const r of rows) {
      const date = r['date'] as string;
      if (!date) continue;
      const month = date.slice(0, 7); // YYYY-MM
      const existing = buckets.get(month) ?? { count: 0, revenue: 0 };
      existing.count += 1;
      existing.revenue += Number(r['pttc'] ?? 0);
      buckets.set(month, existing);
    }
    const monthlyCommandes = [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, v]) => ({ month, count: v.count, revenue: v.revenue }));

    return {
      totalCommandes: totals.totalCommandes,
      totalDevis: devisCount ?? 0,
      totalRevenue: totals.totalRevenue,
      totalCommission: totals.totalCommission,
      pendingCommandes: totals.pendingCommandes,
      monthlyCommandes,
    };
  }

  private async getArticleMap(articleIds: string[]): Promise<Map<string, ArticleRow>> {
    const ids = [...new Set(articleIds.filter(Boolean))];
    if (ids.length === 0) return new Map();
    const { data, error } = await (this.supabase as any)
      .from('authorized_articles')
      .select('id, name, purchase_price_ht, supplier_discount')
      .in('id', ids);
    if (error) throw error;
    return new Map(
      ((data ?? []) as Array<Record<string, unknown>>).map((row) => [
        row['id'] as string,
        {
          name: row['name'] as string,
          // Coût net catalogue (net de remise fournisseur) — uniquement un
          // REPLI pour une ligne sans snapshot ; le calcul vit dans @erp/domain.
          netCostHt: netUnitCostHt({
            purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
            supplierDiscount: Number(row['supplier_discount'] ?? 0),
          }),
        },
      ]),
    );
  }

  private async updateLinkedDelivery(
    deliveryId: string,
    code: string,
    proCustomerId: string,
    input: UpdateProCommandeInput,
    frozenCosts: Array<number | null>,
    articleMap: Map<string, ArticleRow>,
  ): Promise<void> {
    const totalDiscount = input.lines.reduce((sum, line) => sum + line.montantRemise, 0);
    const requestedDiscount = Math.max(0, ...input.lines.map((line) => line.remise));
    const { data: discountDecision, error: discountError } = await (this.supabase as any).rpc(
      'authorize_sales_discount',
      {
        p_doc_type: 'delivery',
        p_action: 'edit',
        p_document_id: deliveryId,
        p_client_request_id: null,
        p_requested_percent: requestedDiscount,
        p_supervisor_token: null,
      },
    );
    if (discountError) throw discountError;
    if (discountDecision?.allowed !== true) {
      throw new Error(`discount_limit_exceeded:${Number(discountDecision?.roleLimit ?? 0)}`);
    }
    const { error: docError } = await (this.supabase as any)
      .from('sales_documents')
      .update({
        notes: input.notes ?? null,
        total_ht: input.pht,
        total_discount: totalDiscount,
        total_vat: input.tva,
        total_ttc: input.pttc,
        delivery_fee: input.deliveryFee,
        net_to_pay: input.totalAPayer,
        pro_commande_code: code,
        customer_source: 'pro',
        client_id: null,
        online_customer_id: null,
        pro_customer_id: proCustomerId,
        pro_client_id: input.proClientId ?? null,
      })
      .eq('id', deliveryId);
    if (docError) throw docError;

    const { error: deleteError } = await (this.supabase as any)
      .from('sales_document_lines')
      .delete()
      .eq('document_id', deliveryId);
    if (deleteError) throw deleteError;

    if (input.lines.length > 0) {
      const { error: insertError } = await (this.supabase as any)
        .from('sales_document_lines')
        .insert(input.lines.map((line, idx) => {
          const article = articleMap.get(line.articleId);
          const quantity = Math.max(line.quantite, 0);
          return {
            document_id: deliveryId,
            line_number: idx + 1,
            article_id: line.articleId,
            description: article?.name ?? line.articleId,
            quantity: line.quantite,
            unit_price_ht: grossUnitPriceHt(line.pht, quantity, line.remise),
            // Même coût FIGÉ que la ligne de commande — jamais le catalogue
            // vivant (et jamais le brut hors remise fournisseur).
            purchase_price: frozenCosts[idx] ?? 0,
            discount_pct: line.remise,
            vat_rate: line.tva,
            total_ht: line.pht,
            total_ttc: line.pttc,
          };
        }));
      if (insertError) throw insertError;
    }
  }
}

interface ArticleRow {
  name: string;
  netCostHt: number | null;
}

function round3(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

function grossUnitPriceHt(totalHtAfterDiscount: number, quantity: number, discountPct: number): number {
  if (quantity <= 0) return totalHtAfterDiscount;
  const discountFactor = 1 - discountPct / 100;
  if (discountFactor <= 0) return round3(totalHtAfterDiscount / quantity);
  return round3(totalHtAfterDiscount / discountFactor / quantity);
}

function collectArticleIds(rows: Array<Record<string, unknown>>): string[] {
  return rows.flatMap((row) =>
    ((row['lines'] as Array<Record<string, unknown>> | undefined) ?? [])
      .map((line) => line['article_id'] as string),
  );
}

function mapLine(
  row: Record<string, unknown>,
  snapshots?: Map<string, ProArticleSnapshot>,
): ProCommandeLine {
  const articleId = row['article_id'] as string;
  const snapshot = snapshots?.get(articleId);
  return {
    id: row['id'] as string,
    commandeCode: row['commande_code'] as string,
    articleId,
    quantite: Number(row['quantite']),
    pht: Number(row['pht']),
    pttc: Number(row['pttc']),
    tva: Number(row['tva'] ?? 19),
    remise: Number(row['remise'] ?? 0),
    montantRemise: Number(row['montant_remise'] ?? 0),
    lineNumber: Number(row['line_number'] ?? 0),
    unitCostHt: row['unit_cost_ht'] != null ? Number(row['unit_cost_ht']) : null,
    articleName: snapshot?.name ?? null,
    articleCode: snapshot?.code ?? null,
    articleImageUrl: snapshot?.imageUrl ?? null,
  };
}

function mapCommandeSummary(row: Record<string, unknown>): ProCommandeSummary {
  return {
    code: row['code'] as string,
    date: row['date'] as string,
    status: row['status'] as ProCommandeSummary['status'],
    proCustomerId: row['pro_customer_id'] as string,
    proClientId: (row['pro_client_id'] as string) ?? null,
    parentDevisCode: (row['parent_devis_code'] as string) ?? null,
    deliveryCity: (row['delivery_city'] as string) ?? null,
    pht: Number(row['pht'] ?? 0),
    pttc: Number(row['pttc'] ?? 0),
    totalCommission: Number(row['total_commission'] ?? 0),
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    totalUsed: Number(row['total_used'] ?? 0),
    totalAPayer: Number(row['total_a_payer'] ?? 0),
  };
}

function mapCommande(
  row: Record<string, unknown>,
  snapshots?: Map<string, ProArticleSnapshot>,
): ProCommande {
  const lines = (row['lines'] as Array<Record<string, unknown>> | undefined) ?? [];
  const client = row['client'] as Record<string, unknown> | null | undefined;
  return {
    code: row['code'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    proClientId: (row['pro_client_id'] as string) ?? null,
    parentDevisCode: (row['parent_devis_code'] as string) ?? null,
    date: row['date'] as string,
    status: row['status'] as ProCommande['status'],
    pht: Number(row['pht'] ?? 0),
    pttc: Number(row['pttc'] ?? 0),
    tva: Number(row['tva'] ?? 0),
    tauxRemise: Number(row['taux_remise'] ?? 0),
    typeRemise: row['type_remise'] as ProCommande['typeRemise'],
    totalCommission: Number(row['total_commission'] ?? 0),
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    totalUsed: Number(row['total_used'] ?? 0),
    totalAPayer: Number(row['total_a_payer'] ?? 0),
    usedSolde: Boolean(row['used_solde']),
    deliveryAddress: (row['delivery_address'] as string) ?? null,
    deliveryCity: (row['delivery_city'] as string) ?? null,
    deliveryPostalCode: (row['delivery_postal_code'] as string) ?? null,
    deliveryComplement: (row['delivery_complement'] as string) ?? null,
    deliverySalesDocumentId: (row['delivery_sales_document_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines.map((line) => mapLine(line, snapshots)).sort((a, b) => a.lineNumber - b.lineNumber),
    client: client
      ? {
          id: client['id'] as string,
          tenantId: client['tenant_id'] as string,
          proCustomerId: client['pro_customer_id'] as string,
          nom: client['nom'] as string,
          prenom: (client['prenom'] as string) ?? null,
          email: (client['email'] as string) ?? null,
          telephone: (client['telephone'] as string) ?? null,
          adresse: (client['adresse'] as string) ?? null,
          ville: (client['ville'] as string) ?? null,
          cp: (client['cp'] as string) ?? null,
          complement: (client['complement'] as string) ?? null,
          depense: Number(client['depense'] ?? 0),
          commission: Number(client['commission'] ?? 0),
          soldeCumule: Number(client['solde_cumule'] ?? 0),
          notes: (client['notes'] as string) ?? null,
          createdAt: client['created_at'] as string,
          updatedAt: client['updated_at'] as string,
        }
      : null,
  };
}
